import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { waLink } from "../lib/whatsapp";
import {
  Search,
  Package,
  CheckCircle2,
  Clock3,
  Truck,
  XCircle,
  MessageCircle,
  RotateCcw,
} from "lucide-react";

const STATUS_STEPS = [
  { key: "new", label: "Order Placed", icon: Package },
  { key: "confirmed", label: "Confirmed", icon: CheckCircle2 },
  { key: "processing", label: "Processing", icon: Clock3 },
  { key: "printed", label: "Printed", icon: Package },
  { key: "ready", label: "Ready for Dispatch", icon: CheckCircle2 },
  { key: "shipped", label: "Shipped", icon: Truck },
  { key: "delivered", label: "Delivered", icon: CheckCircle2 },
];

const STATUS_ORDER = [
  "new",
  "confirmed",
  "processing",
  "printed",
  "ready",
  "shipped",
  "delivered",
];

const STATUS_HELP = {
  new: "We have received your order. Check the payment status below for confirmation.",
  confirmed: "Your order has been checked and confirmed.",
  processing: "Your garment is being prepared and printed.",
  printed: "Printing is complete and your order is being prepared for dispatch.",
  ready: "Your order is packed and ready to be handed to the courier.",
  shipped: "Your order has been handed to the courier.",
  delivered: "Your order has been marked as delivered.",
};

function formatStatus(status) {
  return (status || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function TrackOrder() {
  const [orderNumber, setOrderNumber] = useState("");
  const [mobile, setMobile] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const clearLookup = () => {
    setOrderNumber("");
    setMobile("");
    setOrder(null);
    setError("");
  };

  const handleTrack = async (e) => {
    e.preventDefault();

    const number = orderNumber.trim().toUpperCase();
    const normalizedMobile = mobile.replace(/\D/g, "");

    if (!number) {
      setError("Please enter your order number.");
      return;
    }

    if (normalizedMobile.length !== 10) {
      setError("Please enter the 10-digit mobile number used for the order.");
      return;
    }

    setLoading(true);
    setError("");
    setOrder(null);

    try {
      const response = await api.post("/orders/track", {
        order_number: number,
        mobile: normalizedMobile,
      });

      setOrder(response.data);
    } catch (err) {
      if ([403, 404].includes(err.response?.status)) {
        // The backend intentionally uses the same response for unknown orders
        // and mobile mismatches; keep older backend responses equally generic.
        setError(
          "Order could not be found with those details. Check the order number and mobile number."
        );
      } else {
        setError(
          "Unable to check your order right now. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const currentStatus = order?.status?.toLowerCase() || "";
  const currentIndex = STATUS_ORDER.indexOf(currentStatus);
  const isCancelled = currentStatus === "cancelled";

  return (
    <div className="min-h-[70vh] bg-[#FAF7F2]">
      <section className="border-b border-[#E6DFD5]">
        <div className="container-pmt py-16 sm:py-20 text-center">
          <div className="overline text-[#D95328]">ORDER TRACKING</div>

          <h1 className="display-h1 mt-3">
            Track Your <span className="text-[#D95328]">Order.</span>
          </h1>

          <p className="mt-5 text-stone-600 max-w-xl mx-auto">
            Enter your PrintMyTees order number and mobile number to see the
            latest status of your order.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="container-pmt max-w-2xl px-4 sm:px-0">
          <form onSubmit={handleTrack} className="card-pmt p-4 sm:p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="track-order-number"
                  className="block text-sm font-semibold uppercase tracking-wide mb-3"
                >
                  Order Number <span className="text-[#D95328]">*</span>
                </label>
                <input
                  id="track-order-number"
                  type="text"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="Example: PMT-2026-0001"
                  autoComplete="off"
                  required
                  className="w-full border border-[#D8D0C5] bg-white px-4 py-3 text-base outline-none focus:border-[#D95328]"
                />
              </div>

              <div>
                <label
                  htmlFor="track-mobile"
                  className="block text-sm font-semibold uppercase tracking-wide mb-3"
                >
                  Mobile Number <span className="text-[#D95328]">*</span>
                </label>
                <input
                  id="track-mobile"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  value={mobile}
                  onChange={(e) =>
                    setMobile(e.target.value.replace(/\D/g, ""))
                  }
                  placeholder="10-digit mobile number"
                  autoComplete="tel"
                  required
                  className="w-full border border-[#D8D0C5] bg-white px-4 py-3 outline-none focus:border-[#D95328]"
                />
              </div>
            </div>

            <div className="mt-3 flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full sm:w-auto justify-center"
              >
                <Search size={17} />
                {loading ? "Checking..." : "Track Order"}
              </button>

              {(order || error) && (
                <button
                  type="button"
                  onClick={clearLookup}
                  className="btn-outline-pmt w-full sm:w-auto justify-center"
                >
                  <RotateCcw size={16} />
                  Clear
                </button>
              )}
            </div>

            <p className="mt-3 text-xs text-stone-500">
              Enter the mobile number used when placing the order. This helps
              keep order details private.
            </p>

            {error && (
              <div className="mt-5 flex items-start gap-3 border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                <XCircle size={18} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
          </form>
        </div>
      </section>

      {order && (
        <section className="pb-20">
          <div className="container-pmt max-w-4xl">
            <div className="card-pmt bg-white p-4 sm:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-[#E6DFD5] pb-6">
                <div className="min-w-0">
                  <div className="overline">ORDER NUMBER</div>
                  <div className="mt-1 font-mono text-lg font-bold break-all">
                    {order.order_number}
                  </div>
                </div>

                <div className="sm:text-right">
                  <div className="overline">CURRENT STATUS</div>
                  <div
                    className={`mt-1 inline-flex items-center px-3 py-1 text-sm font-bold uppercase ${isCancelled
                      ? "bg-red-100 text-red-700"
                      : "bg-[#F3EDE2] text-[#D95328]"}`}
                  >
                    {formatStatus(order.status)}
                  </div>
                </div>
              </div>

              {isCancelled ? (
                <div className="mt-8 border border-red-200 bg-red-50 p-5">
                  <div className="flex items-center gap-3 text-red-700 font-bold">
                    <XCircle size={22} />
                    Order Cancelled
                  </div>

                  <p className="mt-2 text-sm text-red-600">
                    Please contact PrintMyTees on WhatsApp if you need more
                    information about this order.
                  </p>

                  <a
                    href={waLink(
                      `Hi PrintMyTees, I need help with my cancelled order ${order.order_number}.`
                    )}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-whatsapp mt-4 inline-flex w-full sm:w-auto justify-center"
                  >
                    <MessageCircle size={17} />
                    Contact us on WhatsApp
                  </a>
                </div>
              ) : (
                <>
                  <div className="mt-10">
                    <div className="overline mb-6">ORDER PROGRESS</div>

                    <div className="space-y-5 sm:space-y-6">
                      {STATUS_STEPS.map((step, index) => {
                        const Icon = step.icon;
                        const completed = currentIndex >= index;
                        const active = currentStatus === step.key;

                        return (
                          <div
                            key={step.key}
                            className="flex items-start gap-3 sm:gap-4"
                          >
                            <div
                              className={`w-10 h-10 shrink-0 flex items-center justify-center border ${completed
                                ? "bg-[#D95328] border-[#D95328] text-white"
                                : "bg-white border-[#D8D0C5] text-stone-400"}`}
                            >
                              <Icon size={19} aria-hidden="true" />
                            </div>

                            <div className="pt-1 min-w-0">
                              <div
                                className={`font-bold uppercase text-sm break-words ${active
                                  ? "text-[#D95328]"
                                  : completed
                                    ? "text-stone-900"
                                    : "text-stone-400"}`}
                              >
                                {step.label}
                                {active && " — Current"}
                              </div>

                              <div className="text-xs text-stone-500 mt-1 break-words">
                                {STATUS_HELP[step.key]}
                              </div>

                              {order.status_history?.[step.key] && (
                                <div className="mt-1 text-xs font-medium text-stone-600 break-words">
                                  {new Date(
                                    order.status_history[step.key]
                                  ).toLocaleString("en-IN", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {Array.isArray(order.items) && order.items.length > 0 && (
                    <div className="mt-10 pt-8 border-t border-[#E6DFD5]">
                      <div className="overline mb-4">ORDER ITEMS</div>

                      <div className="space-y-3">
                        {order.items.map((item, index) => (
                          <div
                            key={index}
                            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-[#E6DFD5] bg-[#FAF7F2] p-4"
                          >
                            <div className="min-w-0">
                              <div className="font-bold break-words">
                                {item.product_name}
                              </div>

                              <div className="mt-1 text-xs text-stone-500 break-words">
                                {[
                                  item.size && `Size: ${item.size}`,
                                  item.color && `Colour: ${item.color}`,
                                  item.printing_method &&
                                    `Print: ${item.printing_method}`,
                                  item.quality && `Quality: ${item.quality}`,
                                ]
                                  .filter(Boolean)
                                  .join(" • ")}
                              </div>

                              {item.gift_options &&
                                Object.entries(item.gift_options).some(([, value]) =>
                                  String(value || "").trim()
                                ) && (
                                  <div className="mt-3 rounded border border-[#E6DFD5] bg-white p-3 text-xs">
                                    <div className="font-semibold text-stone-800 mb-2">
                                      Gift Personalisation
                                    </div>
                                    <div className="space-y-1 text-stone-600">
                                      {Object.entries(item.gift_options)
                                        .filter(([, value]) => String(value || "").trim())
                                        .map(([key, value]) => (
                                          <div key={key} className="break-words">
                                            <span className="font-semibold text-stone-700">
                                              {item.gift_option_labels?.[key] ||
                                                key.replace(/_/g, " ")}:
                                            </span>{" "}
                                            {value}
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}
                            </div>

                            <div className="font-mono font-bold shrink-0">
                              ×{item.quantity}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {order.ekart_tracking_id && (
                    <div className="mt-8 pt-8 border-t border-[#E6DFD5]">
                      <div className="overline mb-4">COURIER TRACKING</div>
                      <div className="border border-[#E6DFD5] bg-[#FAF7F2] p-4 space-y-2">
                        <div className="font-bold">Ekart Logistics</div>
                        <div className="text-sm"><span className="text-stone-500">Tracking ID:</span> <span className="font-mono font-bold">{order.ekart_tracking_id}</span></div>
                        {order.ekart_waybill && <div className="text-sm"><span className="text-stone-500">Waybill:</span> <span className="font-mono font-bold">{order.ekart_waybill}</span></div>}
                        {order.ekart_tracking_url && (
                          <a href={order.ekart_tracking_url} target="_blank" rel="noreferrer" className="btn-outline-pmt mt-2 inline-flex">
                            <Truck size={16} /> Track with Ekart
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {order.payment_status && (
                    <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-t border-[#E6DFD5] pt-5 text-sm">
                      <span className="text-stone-500">Payment Status</span>

                      <span className="inline-flex w-fit rounded-full bg-[#F3EDE2] px-2.5 py-1 font-bold uppercase text-[#D95328]">
                        {formatStatus(order.payment_status)}
                      </span>
                    </div>
                  )}
                </>
              )}

              <div className="mt-8 pt-6 border-t border-[#E6DFD5]">
                <a
                  href={waLink(
                    `Hi PrintMyTees, I want an update on my order ${order.order_number}.`
                  )}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-whatsapp w-full sm:w-auto justify-center inline-flex"
                >
                  <MessageCircle size={17} />
                  Ask about this order on WhatsApp
                </a>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link
                to="/shop"
                className="btn-outline-pmt inline-flex"
              >
                Continue Shopping
              </Link>
              <Link
                to="/"
                className="btn-outline-pmt inline-flex"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
